import React from 'react';
import { flex } from '@fabric-ui/theme';
import { getEmptyFilterExpression, getEmptyTimeBasedFilter } from '../../components/filters/utils';
import { FilterToText } from '../CustomDashboards/components/FiltersDescription';
import { SchemaTypes } from '../Sources/Mapping/types';
import { defaultValues } from './constants';
import { AgingScenarioType, AssignmentType, PredicateType, RuleConfigType, UnificationRule } from './types';

const getSourcePriorityRule = fieldNameWithAlias => ({
  type: RuleConfigType.SOURCE_PRIORITY,
  expression: null,
  sourcePriority: [{ sourceName: undefined, fieldNameWithAlias }],
});

export const emptyRule = (fieldType: SchemaTypes, fieldNameWithAlias?) =>
  ({
    id: crypto.randomUUID(),
    isNew: true,
    name: fieldNameWithAlias ? 'Source Priority Rule' : '',
    ruleConfig: fieldNameWithAlias
      ? getSourcePriorityRule(fieldNameWithAlias)
      : {
          type: RuleConfigType.CONDITIONAL,
          predicate: { type: PredicateType.FILTER, value: getEmptyFilterExpression() },
          assignment: {
            assignmentType: fieldType === SchemaTypes.STRING ? AssignmentType.STRING_TEMPLATE : AssignmentType.VALUE,
            value: defaultValues[fieldType] ?? '',
          },
        },
  }) as UnificationRule;

export const emptyAssetAgingRule = () =>
  ({
    id: crypto.randomUUID(),
    isNew: true,
    name: '',
    ruleConfig: {
      type: RuleConfigType.AGING_CONFIG,
      populationFilter: getEmptyFilterExpression(),
      scenarioType: AgingScenarioType.TIME_BASED,
      timeBasedScenario: getEmptyTimeBasedFilter(),
    },
  }) as UnificationRule;

const defaultAssignmentType = {
  [SchemaTypes.STRING]: AssignmentType.STRING_TEMPLATE,
  [SchemaTypes.NUMBER]: AssignmentType.EMPTY,
  [SchemaTypes.BOOL]: AssignmentType.EMPTY,
};

export const getEmptyRuleSet = (name, type, field, isFieldDefault?) => ({
  ...getEmptyBaseRuleSet(name, field),
  rules: [
    {
      name: 'Default Rule',
      ruleConfig: {
        type: RuleConfigType.CONDITIONAL,
        assignment: {
          assignmentType: isFieldDefault ? AssignmentType.FIELD : defaultAssignmentType[type] || AssignmentType.VALUE,
          value: isFieldDefault
            ? field.value
            : defaultAssignmentType[type] === AssignmentType.EMPTY
              ? undefined
              : defaultValues[type] === '' && ![SchemaTypes.DATETIME, SchemaTypes.DATE].includes(type)
                ? 'Unknown'
                : defaultValues[type],
        },
      },
      id: crypto.randomUUID(),
      isNew: true,
    },
  ],
});

export const unificationRulesListHeadCells = (fieldMap, projectionName) => [
  { label: 'Name', formatter: ({ name }) => name, width: '300px' },
  {
    label: 'Result',
    formatter: ({ ruleConfig }) =>
      ruleConfig.assignment.assignmentType === AssignmentType.FIELD
        ? (fieldMap[ruleConfig.assignment.value].title ?? 'Unknown')
        : ruleConfig.assignment.value?.toString(),
    width: '250px',
  },
  {
    label: 'Condition',
    sx: { overflow: 'hidden' },
    formatter: rule =>
      rule.ruleConfig.predicate &&
      (rule.ruleConfig.predicate.type === PredicateType.FILTER ? (
        <FilterToText filter={rule.ruleConfig.predicate.value} projectionId={projectionName} size="medium" oneLine />
      ) : (
        rule.ruleConfig.predicate.value
      )),
  },
];

export const getEmptyBaseRuleSet = (name, field) => ({
  name,
  ruleSetConfig: {
    projId: field.mainProjId,
    field: field.name,
  },
  rules: [] as UnificationRule[],
});

export const categoryOptions = [
  { title: 'Aging by time', value: AgingScenarioType.TIME_BASED },
  { title: 'Aging by value', value: AgingScenarioType.VALUE_BASED },
];

export const innerLabelStyle = {
  ...flex.itemsCenter,
  color: ({ palette }) => palette.colors.neutrals[600],
  position: 'relative',
  right: 6,
  fontSize: 13,
  fontWeight: 400,
  mr: '2px',
  '.MuiAutocomplete-inputRoot &': { mr: '-6px' },
  ':before': {
    content: '""',
    backgroundColor: ({ palette }) => palette.colors.neutrals[200],
    width: 'calc(100% + 17px)',
    position: 'absolute',
    top: -6,
    bottom: -6,
    left: -9,
    zIndex: -1,
    borderBottomLeftRadius: 10,
    borderTopLeftRadius: 10,
  },
};
